import React, { useState, useRef, useEffect } from 'react'
import styled from 'styled-components'
import { createPortal } from 'react-dom'

const Wrapper = styled.div`
  display: flex;
  flex-direction: column;
  gap: 6px;
  width: 100%;
  position: relative;
`

const Label = styled.label`
  font-size: 13px;
  color: ${({ theme }) => theme.colors.muted};
`

const Trigger = styled.div`
  width: 100%;
  border: 1px solid ${({ theme, $isOpen }) => $isOpen ? theme.colors.primary : theme.colors.border};
  border-radius: ${({ theme }) => theme.radii.md};
  height: 40px;
  padding: 0 12px;
  background: white;
  color: ${({ theme }) => theme.colors.text};
  display: flex;
  align-items: center;
  justify-content: space-between;
  cursor: ${({ $disabled }) => $disabled ? 'not-allowed' : 'pointer'};
  opacity: ${({ $disabled }) => $disabled ? 0.6 : 1};
  box-shadow: ${({ theme, $isOpen }) => $isOpen ? `0 0 0 3px ${theme.colors.primary}33` : 'none'};
  transition: all 0.2s ease;
  user-select: none;
`

const OptionsList = styled.div`
  position: fixed;
  background: white;
  border: 1px solid ${({ theme }) => theme.colors.border};
  border-radius: ${({ theme }) => theme.radii.md};
  box-shadow: ${({ theme }) => theme.shadow.lg};
  max-height: 240px;
  overflow-y: auto;
  z-index: 99999;
  padding: 4px;
`

const Option = styled.div`
  padding: 8px 12px;
  cursor: pointer;
  border-radius: ${({ theme }) => theme.radii.sm};
  color: ${({ theme }) => theme.colors.text};
  background: ${({ $isSelected, theme }) => $isSelected ? theme.colors.background : 'transparent'};

  &:hover {
    background: #f1f5f9;
  }
`

const Arrow = styled.span`
  font-size: 10px;
  color: ${({ theme }) => theme.colors.muted};
  transform: ${({ $isOpen }) => $isOpen ? 'rotate(180deg)' : 'rotate(0)'};
  transition: transform 0.2s ease;
`

export function Select({ label, value, onChange, options = [], disabled, placeholder = 'Select...', style, className }) {
  const [isOpen, setIsOpen] = useState(false)
  const [dropdownPos, setDropdownPos] = useState({ top: 0, left: 0, width: 0 })
  const containerRef = useRef(null)
  const triggerRef = useRef(null)
  const optionsRef = useRef(null)

  useEffect(() => {
    function handleClickOutside(event) {
      if (
        containerRef.current && !containerRef.current.contains(event.target) &&
        (!optionsRef.current || !optionsRef.current.contains(event.target))
      ) {
        setIsOpen(false)
      }
    }
    document.addEventListener('mousedown', handleClickOutside)
    return () => document.removeEventListener('mousedown', handleClickOutside)
  }, [])

  useEffect(() => {
    if (!isOpen) return
    function handleResize() { setIsOpen(false) }
    window.addEventListener('resize', handleResize)
    return () => window.removeEventListener('resize', handleResize)
  }, [isOpen])

  const handleOpen = () => {
    if (disabled) return
    if (!isOpen && triggerRef.current) {
      const rect = triggerRef.current.getBoundingClientRect()
      const spaceBelow = window.innerHeight - rect.bottom
      const dropdownMaxHeight = 240 + 8 // max-height + padding
      const openAbove = spaceBelow < dropdownMaxHeight && rect.top > spaceBelow
      if (openAbove) {
        setDropdownPos({ bottom: window.innerHeight - rect.top + 4, top: 'auto', left: rect.left, width: rect.width, openAbove: true })
      } else {
        setDropdownPos({ top: rect.bottom + 4, bottom: 'auto', left: rect.left, width: rect.width, openAbove: false })
      }
    }
    setIsOpen(!isOpen)
  }

  const handleSelect = (val) => {
    if (disabled) return
    onChange && onChange(val)
    setIsOpen(false)
  }

  const selectedOption = options.find(opt => {
    const val = typeof opt === 'object' ? opt.value : opt
    return val === value
  })

  const displayValue = selectedOption
    ? (typeof selectedOption === 'object' ? selectedOption.label : selectedOption)
    : placeholder

  return (
    <Wrapper ref={containerRef} style={style} className={className}>
      {label && <Label>{label}</Label>}
      <Trigger
        ref={triggerRef}
        $isOpen={isOpen}
        $disabled={disabled}
        onClick={handleOpen}
      >
        <span>{displayValue}</span>
        <Arrow $isOpen={isOpen}>▼</Arrow>
      </Trigger>

      {isOpen && createPortal(
        <OptionsList ref={optionsRef} style={{
          ...(dropdownPos.openAbove
            ? { bottom: dropdownPos.bottom, top: 'auto' }
            : { top: dropdownPos.top, bottom: 'auto' }),
          left: dropdownPos.left,
          width: dropdownPos.width,
        }}>
          {options.map((opt, i) => {
            const val = typeof opt === 'object' ? opt.value : opt
            const lbl = typeof opt === 'object' ? opt.label : String(opt)
            const isSelected = val === value

            return (
              <Option
                key={`${val}-${i}`}
                $isSelected={isSelected}
                onClick={() => handleSelect(val)}
              >
                {lbl}
              </Option>
            )
          })}
        </OptionsList>,
        document.body
      )}
    </Wrapper>
  )
}

